'use client';

import { Typography } from '@musinsa/mds';
import type { ProductSalesRankingResponseDTO } from '@musinsa/services-curator/src/sales-ranking/sales-product-ranking.dto';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { FC } from 'react';
import { PageViewTracker } from '../../common/components';
import * as InfiniteList from '../../common/components/InfiniteList';
import { TrackingDataProvider } from '../../common/components/TrackingDataProvider';
import { getCurrentTime } from '../../common/utils';
import { salesRankingFactory } from '../../query-factory/salesRanking.factory';
import { ProductRankingItem } from '../components';
import {
  SalesRankingFilterProvider,
  useSalesRankingFilter,
} from './SalesRankingFilterSection/SalesRankingFilterProvider';
import { SalesRankingFilterSection } from './SalesRankingFilterSection/SalesRankingFilterSection';

const RankingList: FC = () => {
  const { filter } = useSalesRankingFilter();
  const {
    data: productSalesRankingData,
    hasNextPage,
    isPending,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    ...salesRankingFactory.productSalesRanking({
      year: filter.month.year,
      month: filter.month.month,
      sort: filter.orderBy,
      size: 20,
    }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: ProductSalesRankingResponseDTO) =>
      lastPage.pagination.hasNextPage ? lastPage.pagination.currentPage + 1 : null,
  });

  if (isPending) {
    return null;
  }

  const items = productSalesRankingData?.pages.flatMap((page) => page.list) ?? [];
  return items.length > 0 ? (
    <InfiniteList.Root
      className="flex flex-col bg-neutral-color-white pt-4 w-full grow pb-[72px]!"
      loadMore={() => {
        fetchNextPage();
      }}
      isPending={isPending || isFetchingNextPage}
      hasMore={hasNextPage}
    >
      <TrackingDataProvider
        data={{
          page_name: 'curator_product_sales_ranking',
          section_name: 'ProductSalesRanking',
          section_title: '판매 상품 랭킹',
          apply_category_name_list: filter.month.label,
        }}
      >
        {items.map((item, index) => (
          <li key={item.linkUrl} className="px-[16px]!">
            <ProductRankingItem
              rank={index + 1}
              imageUrl={item.imageUrl}
              brandName={item.brandName}
              goodsName={item.goodsName}
              goodsNo={item.goodsNo}
              price={item.price}
              totalQuantity={item.totalQuantity}
              totalCommission={item.totalCommission}
              linkUrl={item.linkUrl}
            />
          </li>
        ))}
      </TrackingDataProvider>
      <InfiniteList.Trigger />
    </InfiniteList.Root>
  ) : (
    <div className="flex flex-col flex-1 w-full items-center justify-center space-y-0.5 grow">
      <Typography size="14" weight="regular">
        판매 상품이 없습니다.
      </Typography>
      <Typography variant="body13pxReg" className="text-base-gray-9">
        상품링크를 공유해보세요
      </Typography>
    </div>
  );
};

export const SalesRankingDetailSection: FC = () => (
  <SalesRankingFilterProvider>
    <div className="flex flex-col flex-1 w-full grow bg-gray-100">
      <SalesRankingFilterSection />
      <RankingList />
    </div>
    {/* NOTE: mss-curator 의 sales-ranking 페이지는 amplitude screen 발화가 없었음 — Musinsa 발화 skip */}
    <PageViewTracker
      pageName="curator_product_sales_ranking"
      musinsaPageName=""
      snowplowProperties={{
        // NOTE: 페이지 뷰 초기 값 전달. apply_category_name_list에는 선택된 랭킹 기간을 전달 (=기본값, 현재 년월)
        apply_category_name_list: getCurrentTime('YYYY.MM'),
      }}
    />
  </SalesRankingFilterProvider>
);
